import { useCallback, useState } from 'react';

import { poolAutomationClient } from '../api';
import {
  usePoolAutoInvite,
  usePoolSave,
  useSwitchChoice,
  useUnfilledNotice,
} from '../hooks';

import { AutomationSwitch, InlineWarning } from './AutomationSwitch';

import SurgeToast, {
  SurgeToastMessage,
} from '@/routes/Agency/PaySurge/components/SurgeToast';

/*
 * GRAVY-10898 — two per-client automation switches in the client modal's
 * Rates tab, under Auto backups and built the same way (ClientBackupsSetting).
 *
 * "Auto-invite preferred pool" (PD-1.x): when one of this client's jobs is
 * POSTED, GravyWork itself texts eligible preferred pool members (wave 1, of
 * the client's wave size or the default), invites more at each surge step,
 * then opens the job to the marketplace when the pool runs out or 3 hours
 * before start, and alerts ops at 2 hours if it is still unfilled. Turning it
 * on reaches only jobs posted AFTER now (graphy stamps `enabled_at`); jobs
 * already posted are left alone, so the warning must say so. Long-term
 * assignments are never auto-invited. The global kill switch
 * (`automation_on`) overrides it: off, nothing is auto-invited anywhere.
 *
 * "Email the client when a shift is unfilled 2 hours before start": one email
 * per shift to the client's admins. It is what GravyWork tells the client, so
 * it is a tenant-admin decision like surge pay.
 *
 * Both are saved on their own endpoints the moment their warning is
 * confirmed, not with the modal's Save: the warning IS the confirmation, and
 * holding it for Save would let Cancel quietly drop a confirmed choice.
 */

type Props = { customerId: number | string };

const PoolAutoInviteSetting = ({ customerId }: Props) => {
  const { data, isError } = usePoolAutoInvite(customerId);
  const [toast, setToast] = useState<SurgeToastMessage | null>(null);
  const dismiss = useCallback(() => setToast(null), []);

  const { save, saving, error } = usePoolSave(
    (enabled: boolean) =>
      poolAutomationClient.setPoolAutoInvite(customerId, enabled),
    'Could not save pool auto-invite. Please try again.',
    (saved) =>
      setToast(
        saved.enabled
          ? {
              kind: 'on',
              title: 'Pool auto-invite turned on',
              message: 'For this client’s jobs posted from now on.',
            }
          : {
              kind: 'off',
              title: 'Pool auto-invite turned off',
              message:
                'No new automatic pool invites on this client’s jobs. Invites already sent stay.',
            },
      ),
  );
  const chooser = useSwitchChoice(save);

  if (isError) {
    return (
      <p className="dashv2-bkpset-error" role="alert">
        Could not load pool auto-invite. Close and try again.
      </p>
    );
  }
  if (!data) return null;

  const waveText =
    data.wave_size != null
      ? `${data.wave_size} ${data.wave_size === 1 ? 'worker' : 'workers'}`
      : (data.default_wave_size ?? 'the default size');

  return (
    <div
      className="dashv2-bkpset-block"
      data-testid="client-pool-auto-invite-section"
    >
      <AutomationSwitch
        label="Auto-invite preferred pool"
        value={data.enabled}
        saving={saving}
        pending={chooser.choice != null}
        error={error}
        onToggle={chooser.request}
        meta={
          data.automation_on
            ? []
            : [
                {
                  text: 'The pool auto-invite master switch is off',
                  testId: 'client-pool-auto-invite-switch-off',
                },
              ]
        }
        testId="client-pool-auto-invite"
      />
      {chooser.choice != null && (
        <InlineWarning
          className="dashv2-bkpconfirm"
          title={`Turn ${chooser.choice ? 'on' : 'off'} pool auto-invite for this client?`}
          goLabel={`Turn ${chooser.choice ? 'on' : 'off'} for this client`}
          onCancel={chooser.cancel}
          onConfirm={chooser.confirm}
          testId="client-pool-auto-invite-confirm"
        >
          {chooser.choice ? (
            <ul>
              <li>
                When a job is posted, GravyWork invites eligible preferred pool
                members automatically (wave size: {waveText}).
              </li>
              <li>It invites more at each surge step.</li>
              <li>
                It opens the job to the marketplace when the pool runs out or 3
                hours before start.
              </li>
              <li>
                Only jobs posted from now on. Jobs already posted are not
                auto-invited.
              </li>
              <li>Texts at night wait until 8 AM.</li>
              {!data.automation_on && (
                <li>
                  The pool auto-invite master switch is off, so this starts when
                  it is turned on.
                </li>
              )}
            </ul>
          ) : (
            <p>
              No new automatic pool invites on this client’s jobs. Invites
              already sent stay.
            </p>
          )}
        </InlineWarning>
      )}
      <p
        className="dashv2-bkpset-help"
        data-testid="client-pool-auto-invite-help"
      >
        {data.enabled
          ? 'On — when a job is posted, GravyWork invites the preferred pool first, then opens it to the marketplace when the pool runs out or 3 hours before start.'
          : 'Off — jobs are not auto-invited to the preferred pool. Long-term assignments never are.'}
      </p>
      <SurgeToast toast={toast} onDismiss={dismiss} />
    </div>
  );
};

const UnfilledNoticeSetting = ({ customerId }: Props) => {
  const { data, isError } = useUnfilledNotice(customerId);
  const [toast, setToast] = useState<SurgeToastMessage | null>(null);
  const dismiss = useCallback(() => setToast(null), []);

  const { save, saving, error } = usePoolSave(
    (enabled: boolean) =>
      poolAutomationClient.setUnfilledNotice(customerId, enabled),
    'Could not save the unfilled shift email. Please try again.',
    (saved) =>
      setToast({
        kind: saved.enabled ? 'on' : 'off',
        title: `Unfilled shift email turned ${saved.enabled ? 'on' : 'off'}`,
        message: saved.enabled
          ? 'The client’s admins are emailed when a shift is unfilled 2 hours before start.'
          : 'The client is not emailed about unfilled shifts.',
      }),
  );
  const chooser = useSwitchChoice(save);

  if (isError) {
    return (
      <p className="dashv2-bkpset-error" role="alert">
        Could not load the unfilled shift email. Close and try again.
      </p>
    );
  }
  if (!data) return null;

  return (
    <div
      className="dashv2-bkpset-block"
      data-testid="client-unfilled-notice-section"
    >
      <AutomationSwitch
        label="Email the client when a shift is unfilled 2 hours before start"
        value={data.enabled}
        saving={saving}
        pending={chooser.choice != null}
        error={error}
        onToggle={chooser.request}
        testId="client-unfilled-notice"
      />
      {chooser.choice != null && (
        <InlineWarning
          className="dashv2-bkpconfirm"
          title={`Turn ${chooser.choice ? 'on' : 'off'} the unfilled shift email for this client?`}
          goLabel={`Turn ${chooser.choice ? 'on' : 'off'} for this client`}
          onCancel={chooser.cancel}
          onConfirm={chooser.confirm}
          testId="client-unfilled-notice-confirm"
        >
          <p>
            {chooser.choice
              ? 'The client’s admins get one email per shift that is still unfilled 2 hours before it starts.'
              : 'The client’s admins are no longer emailed about unfilled shifts.'}
          </p>
        </InlineWarning>
      )}
      <p className="dashv2-bkpset-help">
        {data.enabled
          ? 'On — one email per shift to the client’s admins.'
          : 'Off — the client is not emailed about unfilled shifts.'}
      </p>
      <SurgeToast toast={toast} onDismiss={dismiss} />
    </div>
  );
};

/** Both switches, in the `.dashv2-scope` token wrapper. Tenant admins only. */
const ClientPoolAutomationSettings = ({ customerId }: Props) => (
  <div className="dashv2-scope" data-testid="client-pool-automation-section">
    <PoolAutoInviteSetting customerId={customerId} />
    <UnfilledNoticeSetting customerId={customerId} />
  </div>
);

export default ClientPoolAutomationSettings;
